import * as React from "react"
import { Box, Avatar, IconButton, Tooltip, Chip, Stack } from "@mui/material"
import { IconSeeker } from "@ui-seeker/core"
import { PRICE_ANALYSIS_ROUTES } from "@constants"

import { mappingsModal, selectedBrand } from "../@signals"


const customerColors = [
    '#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#c2185b', 
    '#0097a7', '#5d4037', '#455a64', '#d32f2f', '#512da8'
]

function getCustomerColor(index) {
    return customerColors[index % customerColors.length]
}


export const idSkuColumn = () => ({
    name: 'id_sku',
    label: 'ID SKU',
    options: {
        filter: false,
        sort: true,
        searchable: true,
        setCellProps: () => ({ style: { textAlign: 'center' } }),
        setCellHeaderProps: () => ({ style: { textAlign: 'center' } })
    }
})

export const referenceColumn = () => ({
    name: 'reference',
    label: 'Reference',
    options: {
        filter: false,
        sort: true,
        display: false,
        searchable: true
    }
})

export const nameColumn = () => ({
    name: 'name',
    label: 'SKU',
    options: {
        filter: false,
        sort: true,
        searchable: true,
        setCellProps: () => ({ style: { textAlign: 'center' } }),
        setCellHeaderProps: () => ({ style: { textAlign: 'center' } }),
        customBodyRender: (value) => (
            <Box sx={{ textAlign: 'center' }}>
                {value || '-'}
            </Box>
        )
    }
})

export const eanColumn = () => ({
    name: 'ean',
    label: 'EAN',
    options: {
        filter: false,
        sort: true,
        searchable: true,
        setCellProps: () => ({ style: { textAlign: 'center' } }),
        setCellHeaderProps: () => ({ style: { textAlign: 'center' } })
    }
})

export const companyColumn = () => ({
    name: 'company_name',
    label: 'Company',
    options: {
        filter: false,
        sort: true,
        display: false,
        searchable: true
    }
})

export const brandColumn = () => ({
    name: 'brand_name',
    label: 'Brand',
    options: {
        filter: false,
        sort: true,
        display: false,
        searchable: true
    }
})

export const lineColumn = () => ({
    name: 'line',
    label: 'Line',
    options: {
        filter: true,
        filterType: 'dropdown',
        sort: true,
        searchable: true,
        setCellProps: () => ({ style: { textAlign: 'center' } }),
        setCellHeaderProps: () => ({ style: { textAlign: 'center' } })
    }
})

export const sizeColumn = () => ({
    name: 'size_display',
    label: 'Size',
    options: {
        filter: false,
        sort: true,
        searchable: false,
        setCellProps: () => ({ style: { textAlign: 'center' } }),
        setCellHeaderProps: () => ({ style: { textAlign: 'center' } }),
        customBodyRender: (value, tableMeta) => {
            const rowData = tableMeta.tableData[tableMeta.rowIndex]
            const size = rowData?.size
            const unitySize = rowData?.unity_size
            if (!size) return '-'
            return `${size} ${unitySize || ''}`
        }
    }
})

export const imageColumn = () => ({
    name: 'image_url',
    label: 'Image',
    options: {
        filter: false,
        sort: false,
        searchable: false,
        setCellProps: () => ({ style: { textAlign: 'center' } }),
        setCellHeaderProps: () => ({ style: { textAlign: 'center' } }),
        customBodyRender: (value) => (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Avatar
                    src={value}
                    variant="rounded"
                    sx={{ width: 60, height: 60 }}
                >
                    -
                </Avatar>
            </Box>
        )
    }
})

// Hidden columns for search/filter
export const sizeValueColumn = () => ({
    name: 'size',
    label: 'Size Value',
    options: {
        filter: false,
        sort: false,
        display: false,
        searchable: false
    }
})

export const unitySizeColumn = () => ({
    name: 'unity_size',
    label: 'Unity Size',
    options: {
        filter: false,
        sort: false,
        display: false,
        searchable: false
    }
})

export const customersColumn = () => ({
    name: 'customers',
    label: 'Customers',
    options: {
        filter: true,
        filterType: 'dropdown',
        sort: false,
        searchable: false,
        filterOptions: {
            logic: (value, filterVal) => {
                if (!filterVal || filterVal.length === 0) return false
                if (!value || !Array.isArray(value)) return true
                return !value.some(customer => filterVal.includes(customer))
            }
        },
        setCellProps: () => ({ style: { textAlign: 'center' } }),
        setCellHeaderProps: () => ({ style: { textAlign: 'center' } }),
        customBodyRender: (value) => {
            if (!value || !Array.isArray(value) || value.length === 0) {
                return <span style={{ color: '#9ca3af' }}>-</span>
            }
            return (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" justifyContent="center" useFlexGap>
                    {value.map((customer, index) => (
                        <Chip
                            key={customer}
                            label={customer}
                            size="small"
                            sx={{
                                backgroundColor: getCustomerColor(index),
                                color: '#fff',
                                fontSize: '0.7rem',
                                height: 22
                            }}
                        />
                    ))}
                </Stack>
            )
        }
    }
})

export const mappingsCountColumn = () => ({
    name: 'mappings_count',
    label: 'Mappings',
    options: {
        filter: false,
        sort: true,
        setCellProps: () => ({ style: { textAlign: 'center' } }),
        setCellHeaderProps: () => ({ style: { textAlign: 'center' } }),
        customBodyRender: (value) => (
            <Chip 
                label={value || 0}
                size="small"
                color={value > 0 ? 'success' : 'default'}
                sx={{ fontWeight: 600, minWidth: 40 }}
            />
        )
    }
})

export const actionsColumn = () => ({
    name: 'actions',
    label: '',
    options: {
        filter: false,
        sort: false,
        searchable: false,
        setCellProps: () => ({ style: { textAlign: 'center', width: 90 } }),
        customBodyRender: (value, tableMeta) => {
            // Get the actual row data from rowData array
            const id_sku = tableMeta.rowData[0]
            const name = tableMeta.rowData[4]

            // Get id_brand from tableData
            const rowIndex = tableMeta.currentTableData?.[tableMeta.rowIndex]?.index
            const fullRowData = rowIndex !== undefined ? tableMeta.tableData[rowIndex] : null
            const id_brand = fullRowData?.id_brand || selectedBrand.value

            return (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Edit Mappings">
                        <IconButton
                            size="small"
                            onClick={() => {
                                mappingsModal.value = {
                                    id_sku: id_sku,
                                    name: name,
                                    id_brand: id_brand
                                }
                            }}
                        >
                            <IconSeeker icon="link" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Price Analysis">
                        <IconButton
                            size="small"
                            component="a"
                            href={`${PRICE_ANALYSIS_ROUTES.PATH}?id_sku=${id_sku}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <IconSeeker icon="chart-line" />
                        </IconButton>
                    </Tooltip>
                </Box>
            )
        }
    }
})
